import { type ComponentProps } from "react";

/* ─────────────────────────────────────────────
 * Base SVG wrapper (ensures consistency)
 * ───────────────────────────────────────────── */
function Svg({
    children,
    className,
    ...props
}: ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`block ${className ?? ""}`} // 👈 prevents Safari collapse
            {...props}
        >
            {children}
        </svg>
    );
}

/* ─────────────────────────────────────────────
 * Close Icon
 * ───────────────────────────────────────────── */
export function CloseIcon({ className }: { className?: string }) {
    return (
        <Svg className={className}>
            <path d="M5 5l14 14M19 5L5 19" />
        </Svg>
    );
}

/* ─────────────────────────────────────────────
 * Screenshot Icon
 * ───────────────────────────────────────────── */
export function ScreenshotIcon({
    className,
    ...props
}: React.ComponentProps<"svg">) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className={`block ${className ?? ""}`}
            fill="none"
            {...props}
        >
            <path
                d="M4 9V5h4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />
            <path
                d="M20 9V5h-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />
            <path
                d="M4 15v4h4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />
            <path
                d="M20 15v4h-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            <rect
                x="7"
                y="9"
                width="10"
                height="7"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
            />

            <circle
                cx="12"
                cy="12.5"
                r="2"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
            />

            <path
                d="M9 9l1-1h4l1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />
        </svg>
    );
}

/* ─────────────────────────────────────────────
 * Video Icon
 * ───────────────────────────────────────────── */
export function VideoIcon({ className }: { className?: string }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 20"
            className={`block ${className ?? ""}`}
            fill="currentColor"
        >
            <path d="M17 0a2 2 0 0 1 2 2v2.5l3.5-1.3c.4-.15.85-.15 1.2 0 .4.2.6.55.6.95v11.6c0 .4-.2.75-.6.95-.35.15-.8.15-1.2 0L19 15.5V18a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h15z" />
            <path
                d="M10 7l5 3-5 3z"
                fill="white"
            />
        </svg>
    );
}

/* ─────────────────────────────────────────────
 * Stop Icon
 * ───────────────────────────────────────────── */
export function StopIcon({ className }: { className?: string }) {
    return (
        <Svg className={className} fill="currentColor" stroke="none">
            <rect x="6" y="6" width="12" height="12" rx="2" />
        </Svg>
    );
}

/* ─────────────────────────────────────────────
 * Spinner
 * ───────────────────────────────────────────── */
export function Spinner({ className }: { className?: string }) {
    return (
        <div
            className={`block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin ${className ?? ""}`}
        />
    );
}
